/**
 * surfaceAccess — one role predicate per navigable surface, imported by BOTH the
 * page that enforces it and the rail that offers it.
 *
 * WHY THIS FILE EXISTS (drift found 2026-08-02, hours after the rail role filter
 * shipped). The SPA carried six overlapping "admin" role sets, no two identical:
 *
 *   DashboardPage        core-developer, vertical-product-owner, broker-operator
 *   AdminPage            core-developer, vertical-product-owner
 *   AppOperationsPage    tenant-admin, broker-operator, service-admin, vertical-product-owner
 *   SandboxRequestPage   tenant-admin
 *   catalogOperator      service-admin, broker-operator, vertical-product-owner
 *   useCatalog           tenant-admin, service-admin, broker-operator, vertical-product-owner
 *
 * ruling-2026-08-02-rail-absent-not-denied then wrote the rail's predicates
 * against a SEVENTH notion — isCatalogOperator, which governs the catalogue tile
 * admin row and nothing else. The result was the precise defect that ruling set
 * out to remove, in both directions: the rail offered Dashboard and
 * Administration to a service-admin both pages deny, and hid them from a
 * core-developer both pages admit.
 *
 * The rule this file enforces: A RAIL PREDICATE IS NEVER WRITTEN BY HAND. It is
 * the destination page's own gate, imported. surfaceAccess.test.ts asserts the
 * two agree for every role that exists in the realm.
 *
 * These constants record what each surface ENFORCES TODAY — they are a
 * description, not a proposal. Moving a role between them is a policy change and
 * belongs in the graph first. Two such questions are open right now:
 *   open-task-2026-08-02-broker-operator-role-absent-in-realm
 *     — broker-operator appears in three sets here and in NO realm anywhere.
 *   open-task-2026-08-02-tenant-admin-vs-operator-scope
 *     — the graph calls BOTH tenant-admin and service-admin tenant-scoped, while
 *       catalogOperator treats service-admin as operator-grade to hold
 *       policy-2026-08-01-tenant-admin-never-creates-apps up.
 */

export type HasRole = (role: string) => boolean;

/** Broker-operations dashboard. Platform-side monitoring, not a tenant surface. */
export const DASHBOARD_ROLES: readonly string[] = [
  'core-developer',
  'vertical-product-owner',
  'broker-operator',
];

/** The /admin group: Tenants, Sub-clients, Orders, Subscriptions, Audit log. */
export const ADMIN_SURFACE_ROLES: readonly string[] = [
  'core-developer',
  'vertical-product-owner',
];

/** Filing a sandbox request on the shared vertical broker (PR #32). */
export const SANDBOX_REQUEST_ROLES: readonly string[] = ['tenant-admin'];

/**
 * The per-app App Operations Board (adr-075 step 5). The one management surface
 * a tenant-admin is explicitly a first-class user of, not a guest on.
 */
export const APP_OPERATIONS_ROLES: readonly string[] = [
  'tenant-admin',
  'broker-operator',
  'service-admin',
  'vertical-product-owner',
];

const anyOf = (roles: readonly string[]) => (hasRole: HasRole): boolean =>
  roles.some((r) => hasRole(r));

export const canUseDashboard = anyOf(DASHBOARD_ROLES);
export const canUseAdminSurface = anyOf(ADMIN_SURFACE_ROLES);
export const canRequestSandbox = anyOf(SANDBOX_REQUEST_ROLES);
export const canUseAppOperations = anyOf(APP_OPERATIONS_ROLES);
